import { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { PreJoinPage } from 'pages/PreJoinPage';
import { RoomPage } from 'pages/RoomPage';
import { useParams } from 'react-router-dom';
import { DeepLink, useTheme } from '@web-chat-core/ui-kit';
import { getAppInfo } from '@web-chat-core/meet/src/util/appInfo';
import { ReactComponent as LogoDark } from 'public-images/logo-dark.svg';
import { ReactComponent as LogoLight } from 'public-images/logo-light.svg';
import { JoinPanel } from '../../features/PreJoin';
import { ApplicationLoader } from '../../components/ApplicationLoader';
import { WaitingApprovalPage } from '../WaitingApprovalPage/WaitingApprovalPage';
import { useMainPageViewModel } from './useMainPageViewModel';

const CALL_MODAL_CHOICE_KEY = 'entry_type_call';

export const MainPage = observer(() => {
    const { state, actions } = useMainPageViewModel();
    const { roomName } = useParams<{ roomName: string }>();
    const theme = useTheme();

    const {
        showLoader,
        inRoomNow,
        name,
        isAuthorized,
        connectDisabled,
        isEmptyNameError,
        isParticipantsLimitError,
        isParticipantsLimitRetryLoading,
        isShowWaitingPage,
        isJoinRejected,
    } = state;
    const {
        changeUserName,
        changeRoomConnection,
        connectToRoom,
        retryWaitingRoomJoin,
        retryParticipantsLimit,
        closeParticipantsLimitError,
        prepareDataAndStartRoom,
        dismissJoinRejected,
    } = actions;

    // проверяем, был ли пользователь кикнут - если да, пропускаем диплинк
    const wasKicked = useMemo(() => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('kicked') === '1';
    }, []);

    // если пользователь был кикнут, сразу вызываем prepareDataAndStartRoom без диплинка
    useEffect(() => {
        if (wasKicked && !inRoomNow) {
            prepareDataAndStartRoom();
        }
    }, [wasKicked, inRoomNow, prepareDataAndStartRoom]);

    const content = showLoader ? (
        <ApplicationLoader />
    ) : inRoomNow ? (
        <RoomPage onRoomConnectionChanged={changeRoomConnection} />
    ) : isShowWaitingPage ? (
        <WaitingApprovalPage
            isJoinRejected={isJoinRejected}
            onRetry={retryWaitingRoomJoin}
            onDismissRejected={dismissJoinRejected}
        />
    ) : (
        <PreJoinPage
            isNameReadOnly={isAuthorized}
            userName={name}
            isEmptyNameError={isEmptyNameError}
            isParticipantsLimitError={isParticipantsLimitError}
            isParticipantsLimitRetryLoading={isParticipantsLimitRetryLoading}
            onParticipantsLimitRetry={retryParticipantsLimit}
            onParticipantsLimitClose={closeParticipantsLimitError}
            onChange={changeUserName}
        >
            <JoinPanel isAuthorized={isAuthorized} connectDisabled={connectDisabled} connectToRoom={connectToRoom} />
        </PreJoinPage>
    );

    const logoElement = theme.isDark ? <LogoDark /> : <LogoLight />;

    // если пользователь был кикнут, не показываем диплинк
    if (wasKicked) return <>{content}</>;

    return (
        <DeepLink
            onLinked={prepareDataAndStartRoom}
            choiceSelectKey={CALL_MODAL_CHOICE_KEY}
            relativeDeepUrl={`/inner-call/${roomName}`}
            options={getAppInfo()}
            logoElement={logoElement}
        >
            {content}
        </DeepLink>
    );
});
